import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import useOrganizationData from '../../hooks/useOrganizationData';
import useOrganizationModals from '../../hooks/useOrganizationModals';
import { containerVariants, itemVariants } from '../../utils/animations';

// Tab Components
import DepartmentTab from '../../components/hris/organization/DepartmentTab';
import SubDivisionTab from '../../components/hris/organization/SubDivisionTab';
import PositionTab from '../../components/hris/organization/PositionTab';
import RoleSkillTab from '../../components/hris/organization/RoleSkillTab';
import SalaryTab from '../../components/hris/organization/SalaryTab';

// Modal Components
import DeleteDialog from '../../components/hris/employee/DeleteDialog';
import DepartmentModal from '../../components/hris/organization/modals/DepartmentModal';
import SubDivisionModal from '../../components/hris/organization/modals/SubDivisionModal';
import PositionModal from '../../components/hris/organization/modals/PositionModal';
import RoleModal from '../../components/hris/organization/modals/RoleModal';
import SkillModal from '../../components/hris/organization/modals/SkillModal';
import SalaryGradeModal from '../../components/hris/organization/modals/SalaryGradeModal';
import JobGradeModal from '../../components/hris/organization/modals/JobGradeModal';
import PolicyModal from '../../components/hris/organization/modals/PolicyModal';
import MappingModal from '../../components/hris/organization/modals/MappingModal';

const tabs = [
  { id: 'department', label: 'Divisi' },
  { id: 'subdivision', label: 'Sub Divisi' },
  { id: 'position', label: 'Jabatan' },
  { id: 'role', label: 'Role & Kompetensi' },
  { id: 'salary', label: 'Gaji' },
];

const tabContentVariants = {
  hidden: { opacity: 0, x: 20 },
  visible: { opacity: 1, x: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, x: -20, transition: { duration: 0.2 } },
};

export default function OrganizationStructure() {
  const [activeTab, setActiveTab] = useState('department');
  const { data, loading, saving, loadData, handleDelete, handleSave } = useOrganizationData();
  const { modals, openModal, closeModal, openDeleteDialog } = useOrganizationModals();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <motion.div
      className="space-y-4 md:space-y-6"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants}>
        <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Struktur Organisasi</h1>
        <p className="text-gray-500 mt-1">Kelola hierarki organisasi dan struktur SDM</p>
      </motion.div>

      {/* Tabs */}
      <motion.div variants={itemVariants} className="border-b border-gray-200">
        <div className="flex overflow-x-auto -mb-px">
          {tabs.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-all duration-300 ${
                activeTab === tab.id
                  ? 'border-emerald-500 text-emerald-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </motion.div>

      {/* Tab Content */}
      <AnimatePresence mode="wait">
        <motion.div
          key={activeTab}
          variants={tabContentVariants}
          initial="hidden"
          animate="visible"
          exit="exit"
        >
          {activeTab === 'department' && (
            <DepartmentTab
              departments={data.departments}
              onAdd={() => openModal('department')}
              onEdit={item => openModal('department', item)}
              onDelete={id => openDeleteDialog('department', id)}
            />
          )}

          {activeTab === 'subdivision' && (
            <SubDivisionTab
              subDivisions={data.subDivisions}
              departments={data.departments}
              onAdd={() => openModal('subdivision')}
              onEdit={item => openModal('subdivision', item)}
              onDelete={id => openDeleteDialog('subdivision', id)}
            />
          )}

          {activeTab === 'position' && (
            <PositionTab
              positions={data.positions}
              departments={data.departments}
              subDivisions={data.subDivisions}
              roles={data.roles}
              salaryGrades={data.salaryGrades}
              onAdd={() => openModal('position')}
              onEdit={item => openModal('position', item)}
              onDelete={id => openDeleteDialog('position', id)}
            />
          )}

          {activeTab === 'role' && (
            <RoleSkillTab
              roles={data.roles}
              skills={data.skills}
              onAddRole={() => openModal('role')}
              onEditRole={item => openModal('role', item)}
              onDeleteRole={id => openDeleteDialog('roles', id)}
            />
          )}

          {activeTab === 'salary' && (
            <SalaryTab
              salaryGrades={data.salaryGrades}
              jobGrades={data.jobGrades}
              salaryPolicies={data.salaryPolicies}
              positionMappings={data.positionMappings}
              positions={data.positions}
              departments={data.departments}
              subDivisions={data.subDivisions}
              onAddSalaryGrade={() => openModal('salaryGrade')}
              onEditSalaryGrade={item => openModal('salaryGrade', item)}
              onDeleteSalaryGrade={id => openDeleteDialog('grade', id)}
              onAddJobGrade={() => openModal('jobGrade')}
              onEditJobGrade={item => openModal('jobGrade', item)}
              onDeleteJobGrade={id => openDeleteDialog('jobgrade', id)}
              onAddPolicy={() => openModal('policy')}
              onEditPolicy={item => openModal('policy', item)}
              onDeletePolicy={id => openDeleteDialog('policy', id)}
              onAddMapping={() => openModal('mapping')}
              onDeleteMapping={id => openDeleteDialog('mapping', id)}
              onReload={loadData}
            />
          )}
        </motion.div>
      </AnimatePresence>

      {/* Modals */}
      <DeleteDialog
        show={modals.deleteDialog.open}
        type={modals.deleteDialog.type}
        onClose={() => closeModal('delete')}
        onConfirm={async () => {
          await handleDelete(modals.deleteDialog.type, modals.deleteDialog.id);
          closeModal('delete');
        }}
      />

      <DepartmentModal
        open={modals.departmentModal.open}
        data={modals.departmentModal.data}
        saving={saving}
        onClose={() => closeModal('department')}
        onSave={d => handleSave('department', d, () => closeModal('department'))}
      />

      <SubDivisionModal
        open={modals.subDivisionModal.open}
        data={modals.subDivisionModal.data}
        departments={data.departments}
        saving={saving}
        onClose={() => closeModal('subdivision')}
        onSave={d => handleSave('subdivision', d, () => closeModal('subdivision'))}
      />

      <PositionModal
        open={modals.positionModal.open}
        data={modals.positionModal.data}
        departments={data.departments}
        subDivisions={data.subDivisions}
        roles={data.roles}
        saving={saving}
        onClose={() => closeModal('position')}
        onSave={d => handleSave('position', d, () => closeModal('position'))}
      />

      <RoleModal
        open={modals.roleModal.open}
        data={modals.roleModal.data}
        departments={data.departments}
        subDivisions={data.subDivisions}
        positions={data.positions}
        saving={saving}
        onClose={() => closeModal('role')}
        onSave={d => handleSave('roles', d, () => closeModal('role'))}
      />

      <SkillModal
        open={modals.skillModal.open}
        data={modals.skillModal.data}
        saving={saving}
        onClose={() => closeModal('skill')}
        onSave={d => handleSave('skill', d, () => closeModal('skill'))}
      />

      <SalaryGradeModal
        open={modals.salaryGradeModal.open}
        data={modals.salaryGradeModal.data}
        departments={data.departments}
        subDivisions={data.subDivisions}
        positions={data.positions}
        saving={saving}
        onClose={() => closeModal('salaryGrade')}
        onSave={d => handleSave('salarygrade', d, () => closeModal('salaryGrade'))}
      />

      <JobGradeModal
        open={modals.jobGradeModal.open}
        data={modals.jobGradeModal.data}
        saving={saving}
        onClose={() => closeModal('jobGrade')}
        onSave={d => handleSave('jobgrade', d, () => closeModal('jobGrade'))}
      />

      <PolicyModal
        open={modals.policyModal.open}
        data={modals.policyModal.data}
        saving={saving}
        existingPolicies={data.salaryPolicies}
        onClose={() => closeModal('policy')}
        onSave={async d => {
          const result = await handleSave('policy', d, () => {});
          // Close modal after save, but let PolicyModal update form first if needed
          if (result) {
            closeModal('policy');
          }
        }}
      />

      <MappingModal
        open={modals.mappingModal.open}
        data={modals.mappingModal.data}
        positions={data.positions}
        jobGrades={data.jobGrades}
        saving={saving}
        onClose={() => closeModal('mapping')}
        onSave={d => handleSave('mapping', d, () => closeModal('mapping'))}
      />
    </motion.div>
  );
}
